"use client";

import { useUserPreferences } from "@/hooks/preference/use-user-preferences";
import { useAuth } from "@/hooks/use-auth";
import { useRelationship } from "@/hooks/use-relationship";
import { useMemo } from "react";
import { PROMOTIONAL_CARDS_REGISTRY } from "./registry";
import { CardVisibilityContext, PromotionalCardConfig } from "./types";

/**
 * Get a random selection of cards based on priority weights
 */
export function getRandomCards(count: number, sourceCards: PromotionalCardConfig[]): PromotionalCardConfig[] {
	if (sourceCards.length === 0) return [];
	if (sourceCards.length <= count) return [...sourceCards];

	// Weighted random selection based on priority
	const selected: PromotionalCardConfig[] = [];
	const available = [...sourceCards];

	while (selected.length < count && available.length > 0) {
		const totalWeight = available.reduce((sum, card) => sum + card.priority, 0);
		let random = Math.random() * totalWeight;

		for (let i = 0; i < available.length; i++) {
			random -= available[i].priority;
			if (random <= 0) {
				selected.push(available[i]);
				available.splice(i, 1);
				break;
			}
		}
	}

	return selected;
}

/**
 * Hook to manage and filter promotional cards based on current context.
 *
 * @param pageType - The current page type (search, sup, or project)
 * @returns Object containing eligible cards and utility functions
 */
export function usePromotionalCards(pageType: "search" | "sup" | "project") {
	const { isAuthenticated } = useAuth();
	const { preferences } = useUserPreferences();
	const { isBuyer } = useRelationship();

	// Has preferences means at least one preference exists
	const hasPreferences = preferences.length > 0;

	// Build visibility context
	const context: CardVisibilityContext = useMemo(
		() => ({
			isAuthenticated,
			hasPreferences,
			isBuyer,
			pageType,
		}),
		[isAuthenticated, hasPreferences, isBuyer, pageType],
	);

	// Filter eligible cards based on visibility conditions
	const potentialCards = useMemo(() => {
		return PROMOTIONAL_CARDS_REGISTRY.filter(card => {
			if (!card.pages.includes(pageType)) return false;
			return card.visibilityConditions(context);
		});
	}, [context, pageType]);

	return {
		potentialCards,
		getRandomCards,
		context,
	};
}
